import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Modal,
    Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Files, Pencil, Plus, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { isValidString } from '../../../common/CommonUtilsMethods';
import {
    DisableTrustVaultDocumentSubTypeAgencyUserAPICall,
    ListTrustVaultDocumentSubTypesAgencyUserAPICall,
} from '../api/TrustVaultPhiDocumentsAgencyUserAPI';
import { TrustVaultDocumentSubTypeRespDTO } from '../interfaces/TrustVaultPhiDocumentsAgencyUserInterfaces';
import { DefineTrustVaultDocumentSubTypeModal } from './DefineTrustVaultDocumentSubTypeModal';
import { RenameTrustVaultDocumentSubTypeModal } from './RenameTrustVaultDocumentSubTypeModal';

interface ManageWorkerFileTypesModalProps {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly isMobile: boolean;
}

const ManageWorkerFileTypesModal = ({
    isOpen,
    onClose,
    isMobile,
}: ManageWorkerFileTypesModalProps): React.ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const [fileTypes, setFileTypes] = useState<readonly TrustVaultDocumentSubTypeRespDTO[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);
    const [disablingSubTypeId, setDisablingSubTypeId] = useState<string | undefined>(undefined);
    const [isDefineSubTypeModalOpen, setIsDefineSubTypeModalOpen] = useState<boolean>(false);
    const [fileTypeToRename, setFileTypeToRename] = useState<TrustVaultDocumentSubTypeRespDTO | undefined>(
        undefined
    );

    const loadFileTypes = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await ListTrustVaultDocumentSubTypesAgencyUserAPICall();
            setFileTypes(response?.documentSubTypes ?? []);
        } catch (error) {
            console.error('Failed to load worker file types:', error);
            enqueueSnackbar('Unable to load worker file types', { variant: 'error' });
            setFileTypes([]);
        } finally {
            setIsLoading(false);
            setHasLoaded(true);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (isOpen) {
            void loadFileTypes();
        }
    }, [isOpen, loadFileTypes]);

    const handleSubTypeCreated = (createdSubType: TrustVaultDocumentSubTypeRespDTO): void => {
        setFileTypes((currentFileTypes: readonly TrustVaultDocumentSubTypeRespDTO[]) => {
            const existingIndex: number = currentFileTypes.findIndex(
                (fileType: TrustVaultDocumentSubTypeRespDTO) =>
                    fileType.subTypeUniqueIdentifier === createdSubType.subTypeUniqueIdentifier
            );
            if (existingIndex >= 0) {
                const updatedFileTypes: TrustVaultDocumentSubTypeRespDTO[] = [...currentFileTypes];
                updatedFileTypes[existingIndex] = createdSubType;
                return updatedFileTypes;
            }
            return [...currentFileTypes, createdSubType];
        });
    };

    const handleSubTypeRenamed = (renamedSubType: TrustVaultDocumentSubTypeRespDTO): void => {
        setFileTypes((currentFileTypes: readonly TrustVaultDocumentSubTypeRespDTO[]) => {
            const existingIndex: number = currentFileTypes.findIndex(
                (fileType: TrustVaultDocumentSubTypeRespDTO) =>
                    fileType.subTypeUniqueIdentifier === renamedSubType.subTypeUniqueIdentifier
            );
            if (existingIndex < 0) {
                return currentFileTypes;
            }
            const updatedFileTypes: TrustVaultDocumentSubTypeRespDTO[] = [...currentFileTypes];
            updatedFileTypes[existingIndex] = renamedSubType;
            return updatedFileTypes;
        });
    };

    const handleDisableClick = async (subTypeUniqueIdentifier: string): Promise<void> => {
        if (!isValidString(subTypeUniqueIdentifier)) {
            return;
        }

        setDisablingSubTypeId(subTypeUniqueIdentifier);
        try {
            const response = await DisableTrustVaultDocumentSubTypeAgencyUserAPICall({
                subTypeUniqueIdentifier,
            });
            if (response === null) {
                enqueueSnackbar('Unable to disable file type', { variant: 'error' });
                return;
            }
            enqueueSnackbar('File type disabled', { variant: 'success' });
            setFileTypes((currentFileTypes: readonly TrustVaultDocumentSubTypeRespDTO[]) =>
                currentFileTypes.filter(
                    (fileType: TrustVaultDocumentSubTypeRespDTO) =>
                        fileType.subTypeUniqueIdentifier !== subTypeUniqueIdentifier
                )
            );
        } catch (error) {
            console.error('Failed to disable worker file type:', error);
            enqueueSnackbar('Unable to disable file type', { variant: 'error' });
        } finally {
            setDisablingSubTypeId(undefined);
        }
    };

    return (
        <>
            <Modal
                open={isOpen}
                onClose={onClose}
                aria-labelledby="manage-worker-file-types-modal-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div
                    className="wgc-modal"
                    style={{
                        width: isMobile ? '90%' : '800px',
                        maxHeight: '90vh',
                        outline: 'none',
                    }}
                >
                    <div
                        className="wgc-modal-header"
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                    >
                        <div>
                            <div className="wgc-modal-title" id="manage-worker-file-types-modal-title">
                                Manage Worker File Types
                            </div>
                            <div className="wgc-modal-subtitle">
                                Define reusable file types for worker documents. These labels appear when uploading
                                files and on existing worker documents.
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button
                                type="button"
                                onClick={(): void => {
                                    setIsDefineSubTypeModalOpen(true);
                                }}
                                disabled={isLoading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 16px',
                                    background: '#2c68bd',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap',
                                    opacity: isLoading ? 0.7 : 1,
                                }}
                                onMouseEnter={(event: React.MouseEvent<HTMLButtonElement>): void => {
                                    if (!isLoading) {
                                        event.currentTarget.style.background = '#1e4d8c';
                                    }
                                }}
                                onMouseLeave={(event: React.MouseEvent<HTMLButtonElement>): void => {
                                    event.currentTarget.style.background = '#2c68bd';
                                }}
                            >
                                <Plus size={16} />
                                Add file type
                            </button>
                            <button type="button" className="wgc-modal-close" onClick={onClose}>
                                <CloseRoundedIcon />
                            </button>
                        </div>
                    </div>

                    <div className="wgc-modal-body">
                        {isLoading && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                                <CircularProgress size={32} style={{ color: '#2c68bd' }} />
                            </div>
                        )}
                        {!isLoading && hasLoaded && fileTypes.length === 0 && (
                            <div className="wgc-employment-deduction-config-empty">
                                No worker file types defined yet.
                            </div>
                        )}
                        {!isLoading && fileTypes.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {fileTypes.map((fileType: TrustVaultDocumentSubTypeRespDTO) => (
                                    <div
                                        key={fileType.subTypeUniqueIdentifier}
                                        className="wgc-rate-card"
                                        style={{ display: 'block' }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: '12px',
                                            }}
                                        >
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                    className="wgc-rate-text"
                                                    style={{ fontWeight: 600, textAlign: 'left' }}
                                                >
                                                    {fileType.subTypeLabel}
                                                </div>
                                                {isValidString(fileType.description) && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.5, textAlign: 'left' }}
                                                    >
                                                        {fileType.description}
                                                    </Typography>
                                                )}
                                                {fileType.requiresExpiryDate === true && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}
                                                    >
                                                        Requires expiry date on upload
                                                    </Typography>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Pencil size={16} />}
                                                    onClick={(): void => {
                                                        setFileTypeToRename(fileType);
                                                    }}
                                                    disabled={
                                                        disablingSubTypeId === fileType.subTypeUniqueIdentifier
                                                    }
                                                >
                                                    Rename
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    startIcon={
                                                        disablingSubTypeId === fileType.subTypeUniqueIdentifier ? (
                                                            <CircularProgress size={14} />
                                                        ) : (
                                                            <Trash2 size={16} />
                                                        )
                                                    }
                                                    onClick={(): void => {
                                                        void handleDisableClick(fileType.subTypeUniqueIdentifier);
                                                    }}
                                                    disabled={
                                                        disablingSubTypeId === fileType.subTypeUniqueIdentifier
                                                    }
                                                >
                                                    {disablingSubTypeId === fileType.subTypeUniqueIdentifier
                                                        ? 'Disabling…'
                                                        : 'Disable'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <DefineTrustVaultDocumentSubTypeModal
                open={isDefineSubTypeModalOpen}
                onClose={(): void => {
                    setIsDefineSubTypeModalOpen(false);
                }}
                onSubTypeCreated={handleSubTypeCreated}
            />

            <RenameTrustVaultDocumentSubTypeModal
                open={fileTypeToRename !== undefined}
                fileType={fileTypeToRename}
                onClose={(): void => {
                    setFileTypeToRename(undefined);
                }}
                onSubTypeRenamed={handleSubTypeRenamed}
            />
        </>
    );
};

export { ManageWorkerFileTypesModal };
